import { useAutoAnimate } from '@formkit/auto-animate/react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { StructureImageTypeCode } from 'utils/attachments';
import { resizeImageIfNeeded } from 'utils/helpers';
import { UploadedImage } from './public-image-upload-types';

export type PublicImageUploadPageProps = Readonly<{
  structureUniqueId: string | null;
  imageType: StructureImageTypeCode;
  title: string;
  description: string;
  fileInputId: string;
  acceptedFileTypes: string;
  uploadButtonText: string;
  uploadResultsTitle: string;
  examples?: ReadonlyArray<{
    id: string;
    url: string;
    name: string;
  }>;
  featureImage?: {
    id: string;
    url: string;
    name: string;
  };
  guidanceItems?: readonly string[];
}>;

export default function PublicImageUploadPage({
  structureUniqueId,
  imageType,
  title,
  description,
  fileInputId,
  acceptedFileTypes,
  uploadButtonText,
  uploadResultsTitle,
  examples = [],
  featureImage,
  guidanceItems = []
}: PublicImageUploadPageProps) {
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [animate] = useAutoAnimate({ duration: 100 });
  const exampleCount = examples.length;
  const showGuidance =
    exampleCount > 0 || featureImage !== undefined || guidanceItems.length > 0;

  useEffect(() => {
    if (exampleCount < 2) return undefined;

    const timer = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % exampleCount);
    }, 5000);

    return () => clearInterval(timer);
  }, [exampleCount]);

  const handleImageUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    if (!structureUniqueId) {
      setErrorMessage('Missing structure ID in URL');
      return;
    }

    setIsUploading(true);
    setSuccessMessage(null);
    setErrorMessage(null);

    try {
      const formData = new FormData();
      formData.append('structureUniqueId', structureUniqueId);
      formData.append('imageType', imageType);

      const fileArray = Array.from(files);
      const processedFiles = await Promise.all(
        fileArray.map((file) =>
          file.type.startsWith('image/') ? resizeImageIfNeeded(file) : file
        )
      );

      const newPreviews: UploadedImage[] = [];
      for (const file of processedFiles) {
        formData.append('images', file);
        const name = file.name.split('.')[0];
        if (file.type.startsWith('image/')) {
          const id = `${name}-${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}`;
          newPreviews.push({
            id,
            name,
            previewUrl: URL.createObjectURL(file)
          });
        }
      }

      await axios.post(
        `${process.env.REACT_APP_SYSTEM_SELECTOR_API_URL}public/structure/structure-images`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setUploadedImages((prev) => [...prev, ...newPreviews]);
      setSuccessMessage(
        `Successfully uploaded ${processedFiles.length} file${
          processedFiles.length > 1 ? 's' : ''
        }!`
      );
    } catch (error) {
      console.error('Error uploading images:', error);
      setErrorMessage('Failed to upload images. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  if (!structureUniqueId) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-gray-100'>
        <div className='rounded-lg bg-white p-8 text-center shadow-lg'>
          <div className='text-xl font-semibold text-red-600'>Invalid URL</div>
          <div className='mt-2 text-gray-600'>
            Missing structure ID parameter. Please use a valid link.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-100 py-6 sm:py-10'>
      <div className='mx-auto max-w-[1400px] px-4 sm:px-5'>
        <div className='flex flex-col items-center gap-8' ref={animate}>
          <header className='text-center'>
            <div className='text-2xl font-bold text-gray-900 sm:text-3xl'>
              {title}
            </div>
            <div className='mt-2 text-base text-gray-600 sm:text-xl'>
              {description}
            </div>
          </header>
          {showGuidance && (
            <div className='grid w-full grid-cols-1 items-center justify-items-center gap-4 sm:grid-cols-2 sm:gap-8'>
              {exampleCount > 0 && (
                <div className='flex w-full max-w-[320px] flex-col items-center justify-center gap-2 sm:max-w-[360px] lg:max-w-[440px] xl:max-w-[520px]'>
                  <div className='relative h-[260px] w-full sm:h-[300px] lg:h-[380px] xl:h-[460px]'>
                    {examples.map((example, index) => (
                      <img
                        key={example.id}
                        className={`absolute inset-0 h-full w-full rounded-lg object-cover shadow-md transition-opacity duration-500 ${
                          index === carouselIndex ? 'opacity-100' : 'opacity-0'
                        }`}
                        src={`/images/${example.url}`}
                        alt={example.name}
                      />
                    ))}
                  </div>
                </div>
              )}
              {featureImage && (
                <div className='flex w-full max-w-[320px] flex-col items-center justify-center gap-2 sm:max-w-[360px] lg:max-w-[440px] xl:max-w-[520px]'>
                  <img
                    className='h-[260px] w-full rounded-lg object-cover shadow-md sm:h-[300px] lg:h-[380px] xl:h-[460px]'
                    src={`/images/${featureImage.url}`}
                    alt={featureImage.name}
                  />
                </div>
              )}
              {guidanceItems.length > 0 && (
                <div className='grid w-full max-w-3xl grid-cols-1 gap-3 sm:grid-cols-3'>
                  {guidanceItems.map((item, index) => (
                    <div
                      key={item}
                      className='rounded-lg bg-white p-5 text-center shadow-md'
                    >
                      <div className='mx-auto mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 font-semibold text-blue-700'>
                        {index + 1}
                      </div>
                      <div className='text-sm font-medium text-gray-700 sm:text-base'>
                        {item}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className='flex w-full flex-col items-center gap-3 sm:gap-4'>
            <input
              id={fileInputId}
              type='file'
              accept={acceptedFileTypes}
              onChange={(e) => {
                handleImageUpload(e.currentTarget.files);
                e.currentTarget.value = '';
              }}
              className='hidden'
              multiple
            />
            <label
              htmlFor={fileInputId}
              className={`w-full cursor-pointer rounded-lg px-6 py-3 text-center text-base font-semibold text-white transition-colors sm:w-auto sm:px-8 sm:py-4 sm:text-lg ${
                isUploading
                  ? 'cursor-not-allowed bg-gray-400'
                  : 'bg-blue-500 hover:bg-blue-600'
              }`}
            >
              {isUploading ? 'Uploading...' : uploadButtonText}
            </label>
            {successMessage && (
              <div className='flex items-center gap-2 rounded-lg bg-green-100 px-3 py-2 text-sm text-green-700 sm:px-4 sm:text-base'>
                <CheckCircleIcon className='h-5 w-5 flex-shrink-0' />
                {successMessage}
              </div>
            )}
            {errorMessage && (
              <div className='rounded-lg bg-red-100 px-3 py-2 text-sm text-red-700 sm:px-4 sm:text-base'>
                {errorMessage}
              </div>
            )}
          </div>
          {uploadedImages.length > 0 && (
            <div className='w-full'>
              <div className='mb-3 text-center text-base font-semibold text-gray-700 sm:mb-4 sm:text-lg'>
                {uploadResultsTitle}
              </div>
              <div className='grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:items-start sm:justify-center sm:gap-5'>
                {uploadedImages.map((image) => (
                  <div
                    key={image.id}
                    className='flex flex-col items-center justify-center gap-2'
                  >
                    <img
                      src={image.previewUrl}
                      alt={image.name}
                      className='w-full rounded-lg shadow-md sm:w-[200px]'
                    />
                    <div className='truncate text-xs text-gray-600 sm:text-sm'>
                      {image.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
